import posthog from 'posthog-js';

const projectToken = import.meta.env.VITE_POSTHOG_KEY;
const host =
  import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

// Match project convention: compare to lowercase env (see e.g. utils.js, useGenerateExternalUrl.tsx)
const shouldSampleEvents =
  import.meta.env.VITE_ENVIRONMENT === 'production' ||
  import.meta.env.VITE_ENVIRONMENT === 'staging';

/**
 * Deterministic 0–1 value from a string (same string => same value).
 * Uses FNV-1a for good distribution across UUID-like and sequential inputs.
 */
/* eslint-disable no-bitwise */
function hashToUnit(str: string): number {
  let h = 2166136261; // FNV offset basis (32-bit)
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619); // FNV prime
  }
  return ((h >>> 0) % 1e6) / 1e6;
}
/* eslint-enable no-bitwise */

/**
 * before_send that keeps only a fraction of events by distinct_id (LISD-2217 cost control).
 * Same distinct_id always gets the same decision. See: https://posthog.com/docs/libraries/js/features#sampling-events
 * Reads distinct_id from top level or properties so sampling is reliably per-user.
 * Always passes $identify and $set so PostHog can link anonymous → identified, and always passes our custom (non-$) business events so low-volume funnel events aren't sampled out (LISD-2503); only $-prefixed built-ins ($pageview, $autocapture) are sampled. When no distinct_id, sends all events (avoids binary per event type).
 */
function createEventSamplingBeforeSend(sampleRate: number) {
  return (
    event: {
      event?: string;
      distinct_id?: string;
      properties?: Record<string, unknown>;
    } | null
  ) => {
    if (!event) return null;
    // Always pass identity/linking events so PostHog can link anonymous → identified
    const eventName = event.event;
    if (eventName === '$identify' || eventName === '$set') return event;
    // Always send our custom (business) events; only sample high-volume PostHog built-ins ($pageview, $autocapture).
    // Low-volume funnel events (e.g. intake_started) must never be sampled out — esp. anonymous pre-login ids (LISD-2503).
    if (eventName && !eventName.startsWith('$')) return event;
    const distinctId =
      event.distinct_id ??
      (event.properties?.$distinct_id as string) ??
      (event.properties?.distinct_id as string) ??
      '';
    // When no distinct_id, don't sample by event name (would be binary per event type); send all
    if (!distinctId) return event;
    const value = hashToUnit(distinctId);
    return value < sampleRate ? event : null;
  };
}

/**
 * PostHog client for Product Analytics (LISD-2217).
 * Initialized once when project token is set; used by PostHogProvider.
 * In production and staging, only 10% of events are sent (before_send sampling) to control cost.
 *
 * Cost control docs:
 * - Event sampling (this implementation): https://posthog.com/docs/libraries/js/features#sampling-events
 * - Session recording sampling (configure in PostHog UI): https://posthog.com/docs/session-replay/cutting-costs#sampling
 */
if (projectToken) {
  const initOptions: Parameters<typeof posthog.init>[1] = {
    api_host: host,
    defaults: '2026-01-30', // Versioned defaults: SPA pageviews, script inject in head, session/rageclick tweaks
    person_profiles: 'identified_only', // Only create person profiles after identify(); anonymous events stay cheaper
  };
  if (shouldSampleEvents) {
    // before_send receives full CaptureResult; we pass through or drop (same event object or null).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (initOptions as any).before_send = createEventSamplingBeforeSend(0.1);
  }
  posthog.init(projectToken, initOptions);
}

export default posthog;
